import { useCallback, useContext, useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridFooterContainer,
  GridPagination,
  useGridApiRef,
  GridColumnMenu,
  GridColumnMenuProps,
  GridColumnMenuItemProps,
  GridFilterInputValue,
  GridFilterOperator,
  getGridStringOperators,
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { clearStateIssue, issueState, setStateIssue } from "../../store/IssueSlice";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopSharpIcon from "@mui/icons-material/StopSharp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { RootState } from "../../store/store";
import LinkIcon from "@mui/icons-material/Link";
import DateRangeIcon from "@mui/icons-material/DateRange";
import moment from "moment";
import "./style.scss"
import { setIsMeetingTaskRunning, setTrackingState } from "../../store/trackingSlice";
import { generateOpenProjectTaskUrl, getColorBasedOnDueDate } from "../../utils/issues";
import { CircularProgress, IconButton, ListItemIcon, ListItemText, MenuItem, Tooltip } from "@mui/material";
import { getOpenProjectAllUserTask, getOpenProject_ProjectTasks, getOpenProjectFilteredTasks } from "../../services/openProject.service";
import { AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE } from "../../services/const";
import { getJiraProjectTask } from "../../services/jira.service";
import { getWaymoreCrmTasks } from "../../services/waymoreCrm.service";
import TalkToAiIcon from "../../icons/talkToAi";
import TalkToAiDialog from "../../components/talkToAI/talkToAiDialog";
import _, { cloneDeep } from 'lodash';
import { showNotification } from "../../utils/notification";
import { ProjectContext } from "../Homepage";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { IJiraIssue, IJiraUserDetails, IOpenProjectErrorResponse } from "../../common/interface";
import TimeTrackingDialog from "../../components/TimeTrackingDialog";
import CreateJiraTaskDialog from "../../components/task/createJiraTaskDialog";
import OpenProjectCreateTaskDialog from "../../components/openProject/createOpenProjectTaskDialog";
import { setSearchIssueText } from "../../store/projectSlice";

const sortByStartDate = (taskList: any[]) => {
  taskList.sort((a, b): any => {
    const dataA = a.startDate ? new Date(a.startDate) : new Date('9999-12-31');
    const dataB = b.startDate ? new Date(b.startDate) : new Date('9999-12-31');
    return dataA.getTime() - dataB.getTime();
  })
}
const RefreshTooltipMessage = (
  <div>
    <span>Refresh the table to retrieve the latest data.</span><br />
    <span>Note: The table is automatically updated every 5 minutes.</span>
  </div>
);

const CustomFooter: React.FC<{ onRefresh: () => void, isRefresh: boolean }> = ({ onRefresh, isRefresh }) => (
  <GridFooterContainer>
    <Box display="flex" style={{ marginLeft: '10px' }} justifyContent="space-between" alignItems="center" width="100%">
      <Tooltip placement="right-start" enterTouchDelay={0} arrow title={RefreshTooltipMessage}>
        <button className="reload-icon-btn" disabled={isRefresh} onClick={onRefresh}>
          <RefreshIcon className={isRefresh ? "reload-icon-rotate" : ""} />
        </button>
      </Tooltip>
      <GridPagination />
    </Box>
  </GridFooterContainer>
);

// Function to sort the hierarchy based on a column
const sortHierarchy = (task: any, field: any, direction: any) => {
  task.children.sort((a: any, b: any) => {
    const aValue = a[field];
    const bValue = b[field];

    if (typeof aValue === "string") {
      return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    }
  });

  task.children.forEach((child: any) => sortHierarchy(child, field, direction));
};

const handleTreeToggleOpen = (data: any, selectedNotification: any) => {
  const resource = selectedNotification?._links?.resource;
  const selected: any = { data: "", ids: {}};

  outerLoop:
  for (const parent of data) {
    if (parent?.taskTitle === resource?.title && parent?.workPackagesLink === resource?.href) {
      selected.data = parent;
      selected.ids[parent?.id] = true;
      break outerLoop;
    }
    if (parent?.children?.length > 0) {
      for (const child1 of parent?.children) {
        if (child1?.taskTitle === resource?.title && child1?.workPackagesLink === resource?.href) {
          selected.data = child1;
          selected.ids[parent?.id] = true;
          selected.ids[child1?.id] = true;
          break outerLoop;
        }

        if (child1?.children?.length > 0) {
          for (const child2 of child1?.children) {
            if (child2?.taskTitle === resource?.title && child2?.workPackagesLink === resource?.href) {
              selected.data = child2;
              selected.ids[parent?.id] = true;
              selected.ids[child1?.id] = true;
              selected.ids[child2?.id] = true;
              break outerLoop;
            }
          }
        }
      }
    }
  }

  return selected;
};
function CustomColumnMenu(props: GridColumnMenuItemProps) {
  const { CustomDateHandler, CustomDateFormat } = props;
  return (
    <Tooltip title="Change the format to">
      <MenuItem onClick={CustomDateHandler}>
        <ListItemIcon>
          <DateRangeIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{CustomDateFormat}</ListItemText>
      </MenuItem>
    </Tooltip>
  );
}

const IssueTablePage = ({
  issueRefetch,
  createdMeetingId,
}: {
  issueRefetch: boolean;
  createdMeetingId: string | null;
}) => {
  const issueState = useSelector((state: RootState) => state.issue);
  const projectState = useSelector((state: RootState) => state.project);
  const trackerState = useSelector((state: RootState) => state.tracker);
  const projectSelectedProject = projectState.selectedProject;
  const isIssueTracking = trackerState.isTracking
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const filterIssueTypeId = projectState?.filterIssueTypeId;
  const filterIssueCompleteShow = projectState?.filterIssueCompleteShow;
  const searchIssueText = projectState?.searchIssueText;
  const showCompleted = projectState?.showCompleted;
  const showPending = projectState?.showPending;
  const createdBy = projectState?.createdBy;
  const allOpen = projectState?.allOpen;
  const recentlyCreated = projectState?.recentlyCreated;
  const overDue = projectState?.overDue;
  const startDate = projectState?.startDate;
  const endDate = projectState?.endDate;


  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;
  const [isRefresh, setIsRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [intervalCall, setIntervalCall] = useState<{ refetch?: boolean }>({});

  const [openTalkToAI, setOpenTalkToAI] = useState(false);
  const [issueListAbortController, setIssueListAbortController] = useState(new AbortController());
  const { selectedNotification, setSelectedNotification } = useContext(ProjectContext);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);

  const handleTalkToAiClose = () => {
    setOpenTalkToAI(false);
    setSelectedIssue(null);
  }

  const projectTypeMenu = projectState.projectTypeMenu
  const projectType = projectState.projectType
  const [taskSummary, setTaskSummary] = useState({});
  const [expandedRows, setExpandedRows] = useState<any>({});
  const [sortModel, setSortModel] = useState<any>([]);
  const [cloneTableData, setCloneTableData] = useState<any>([]);
  const [dateFormat, setDateFormat] = useState("DD MMMM YYYY");
  const formatDate = (date: any) => dayjs(date).format(dateFormat);
  const alternativeFormat =
    dateFormat === "DD/MM/YY" ? "DD MMMM YYYY" : "DD/MM/YY";

  const isSubTask = ["Task_", "Subtask_"].includes(issueState?.issueType);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOPTaskDialogOpen, setIsOPTaskDialogOpen] = useState(false);
  const [isJiraTaskDialogOpen, setIsJiraTaskDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleCreateTaskDialogClose = (
    _?: boolean,
    responseData?: {
      id: string;
      key: string;
      self: string;
    }
  ) => {
    setIsDialogOpen(false);
    if (projectType === projectTypeMenu[0]) {
      setIsOPTaskDialogOpen(false);
    } else if (projectType === projectTypeMenu[2]) {
      setIsJiraTaskDialogOpen(false);
    }
    if (responseData?.key) {
      dispatch(setSearchIssueText(responseData.key));
    } else {
      getIssueList();
    }
  };

  const handleTaskCreation = () => {
    if (projectType === projectTypeMenu[0]) {
      setIsOPTaskDialogOpen(true);
    } else if (projectType === projectTypeMenu[2]) {
      setIsJiraTaskDialogOpen(true);
    }
    handleDialogClose();
  };
  const checkTheUserTicket = (taskDetails: IJiraIssue) => {
    const jiraLoginDetails = localStorage.getItem("jiraLoginDetails")
      ? JSON.parse(localStorage.getItem("jiraLoginDetails") || "")
      : "";
    const loggedInUserDeatil: IJiraUserDetails = jiraLoginDetails?.userDetails;
    if (
      loggedInUserDeatil?.accountId === taskDetails?.fields?.assignee?.accountId
    ) {
      return true;
    }
    return false;
  };

  const handleTracking = ({ issueData }: { issueData: IJiraIssue }) => {
    const isTrackingStarted = isIssueTracking;
    const trackingAction = () => {
      dispatch(setTrackingState(true));
      showNotification({ body: `Started Time Tracking` });
    };

    const stopTrackingAction = () => {
      dispatch(setTrackingState(false));
      showNotification({ body: "Tracking Stopped." });
    };

    // If tracking is already on, stop it
    if (isTrackingStarted) {
      stopTrackingAction();
    } else if (projectType === "Jira") {
      if (checkTheUserTicket(issueData)) {
        trackingAction();
      } else {
        handleDialogOpen();
      }
    } else {
      trackingAction();
    }
  };


  // Toggle expand/collapse state for a row
  const toggleExpand = (id: any) => {
    setExpandedRows((prev: any) => ({ ...prev, [id]: !prev[id] }));
  };

  // 🔹 Get visible rows dynamically based on expanded parents
  const getVisibleRows = (data: any) => {
    let visible: any = [];
    data.forEach((row: any) => {
      visible.push(row);
      if (expandedRows[row.id]) {
        visible = visible.concat(getVisibleRows(row.children));
      }
    });
    return visible;
  };

  // Handle sorting event from DataGrid
  const handleSortModelChange = (newModel: any) => {
    setSortModel(newModel);
    if (newModel.length === 0) {
      setSortModel([]); // Keep default reset behavior
      setTableRows(cloneDeep(cloneTableData));
      return;
    }

    const { field, sort } = newModel[0];
    const rootTasks = cloneDeep(tableRows);

    // Apply sorting
    rootTasks.sort((a: any, b: any) => {
      const aValue = a[field];
      const bValue = b[field];

      if (typeof aValue === "string") {
        return sort === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sort === "asc" ? aValue - bValue : bValue - aValue;
      }
    });

    rootTasks.forEach((task: any) => sortHierarchy(task, field, sort));
    setTableRows([...cloneDeep(rootTasks)])
  };

  const handleTalkToAiClick = (rowData: any) => {
    console.log("Clicked row data:", rowData);
    setSelectedIssue(rowData);
    setOpenTalkToAI(true);
  };
  
  const prioritisationPointOperators: GridFilterOperator[] = [
    {
      label: 'greater than',
      value: 'greaterThan',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return (params) =>
          Number(params.value) > Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: 'less than',
      value: 'lessThan',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return (params) =>
          Number(params.value) < Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
  ];
  const columnsObj = {
    actions: {
      field: "actions",
      headerName: "",
      width: 10,
      renderCell: (params: any) => {
        return (
          (params.row.id === issueState.id) && (
            <IconButton onClick={() => handleTracking(params?.row)}>
              {isIssueTracking && issueState.id === params.row.id ? <StopSharpIcon fontSize="large" color="primary"></StopSharpIcon> : <PlayCircleIcon fontSize="large" color="primary"></PlayCircleIcon>}
            </IconButton>
          )
        )
      },
      sortable: false,
    },
    talkToAi: {
      field: "talkToAi",
      headerName: "",
      width: 10,
      renderCell: (params: any) => {
        return (
          <span className="talk-to-ai-icon-container" onClick={() => handleTalkToAiClick(params?.row)}>
            <TalkToAiIcon />
          </span>
        )
      },
      sortable: false,
    },
    issue: {
      field: 'issue',
      headerName: 'Issue',
      width: 380,
      sortable: true
    },
    openProjectIssue: {
      field: 'issue',
      headerName: 'Issue',
      width: 380,
      minWidth: 250,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => {
        const { id, indentLevel, children } = params.row;
        const hasChildren = children?.length > 0;
        return (
          <div style={{ 
            paddingLeft: `${indentLevel * 20}px`, 
            display: "flex", 
            alignItems: "center", 
            width: `calc(100% - ${indentLevel * 20}px)`}}
          >
            <IconButton 
              size="small"
              disabled={!hasChildren }
              onClick={() => toggleExpand(id)}
              className={!hasChildren ? "disable-icon" : ""}
            >
              {expandedRows[id] ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
            <Tooltip title={params.row.issue} arrow>
              <div className="text-overflow-row">{params.row.issue}</div>
            </Tooltip>
          </div>
        );
      },
    },
    link: {
      field: 'LINK',
      headerName: '',
      width: 50,
      renderCell: (params: any) => {
        return (
          <a href={params?.row?.taskWebUrl} target="_blank" rel="noreferrer"><LinkIcon style={{ color: '#188bfd' }} /></a>
        )
      },
      sortable: false
    },
    key: {
      field: 'key',
      headerName: 'Key',
      width: 100,
      sortable: true
    },
    dueDate: {
      field: 'dueDate',
      headerName: 'Due Date',
      width: 150,
      sortable: true,
      renderCell: (params: any) => {
        return params.value !== null ? formatDate(params.value) : null;
      },
    },
    createdAt: {
      field: 'createdAt',
      headerName: 'Created At',
      width: 200,
      sortable: true
    },
    updatedAt: {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 200,
      sortable: true
    },
    priority: {
      field: 'priority',
      headerName: 'Priority',
      width: 150,
      sortable: true
    },
    prioritisationPoints: {
      field: "prioritisationPoints",
      headerName: "Prioritisation Points",
      width: 150,
      sortable: true,
      filterOperators: [...getGridStringOperators(),...prioritisationPointOperators]
    },
    email: {
      field: 'email',
      headerName: 'Email',
      width: 180,
      sortable: true
    },
    startDate: {
      field: 'startDate',
      headerName: 'Start Date',
      width: 200,
      sortable: true
    },
    status: {
      field: 'status',
      headerName: 'Status',
      width: 150,
      sortable: true
    },
    accountName: {
      field: 'accountName',
      headerName: 'Account Name',
      width: 250,
      sortable: true
    },
    issueType: {
      field: 'issueType',
      headerName: 'Issue Type',
      width: 150,
      sortable: true
    },
    createdBy: {
      field: 'createdBy',
      headerName: 'Created By',
      width: 150,
      sortable: true
    },
  }

  const openProjectColumns: GridColDef[] = [
    columnsObj.talkToAi,
    columnsObj.actions,
    columnsObj.openProjectIssue,
    columnsObj.link,
    columnsObj.key,
    columnsObj.dueDate,
    columnsObj.priority,
    columnsObj.issueType,
    columnsObj.createdBy,
    columnsObj.createdAt,
    columnsObj.updatedAt,
  ]

  const jiraColumns: GridColDef[] = [
    columnsObj.talkToAi,
    columnsObj.actions,
    columnsObj.issue,
    columnsObj.link,
    columnsObj.key,
    columnsObj.status,
    columnsObj.dueDate,
    columnsObj.priority,
    columnsObj.prioritisationPoints,
    columnsObj.issueType,
    columnsObj.createdBy,
    columnsObj.createdAt,
    columnsObj.updatedAt,
  ]

  const waymoreCrmColumns: GridColDef[] = [
    columnsObj.talkToAi,
    columnsObj.issue,
    columnsObj.startDate,
    columnsObj.status,
    columnsObj.email,
    columnsObj.accountName,
  ]

  const getDataGridColumns = () => {
    if (projectType === projectTypeMenu[0]) {
      return openProjectColumns;
    } else if (projectType === projectTypeMenu[2]) {
      return jiraColumns;
    } else if (projectType === projectTypeMenu[1]) {
      return waymoreCrmColumns;
    }
    return [];
  };

  const [tableRows, setTableRows] = useState<any>([]);
  const [hoverId, setHoverId] = useState(null);

  const handleJiraAssigneeError = (taskResponse: IOpenProjectErrorResponse): boolean => {
    if (
      taskResponse?.error?.message === "Assignee filter has invalid values."
    ) {
      toast.error("You are not a member of the selected project");
      return true;
    }
    return false;
  };

  const getOpenProjectTaskList = async (isIssueTypeAll: boolean = false, abortController?: AbortController, loader: boolean = true, refreshingInSetTimeOut: boolean = false) => {
    let taskData = []
    let isCanceled: boolean = false;

    try {
      if(loader)setLoading(true);
      setIsRefresh(true)
      let taskResponse
      if (userDetails?.openProjectDetails?.id) {
        let ldapUserId = userDetails?.openProjectDetails?.id
        if (projectSelectedProject?._links?.workPackages?.href) {
          let workPackagesLink = projectSelectedProject?._links?.workPackages?.href
          if (isIssueTypeAll || filterIssueTypeId == 'ALL') {

            if (createdBy || allOpen || recentlyCreated || overDue) {
              taskResponse = await getOpenProjectFilteredTasks({
                id: userDetails?.openProjectDetails?.id,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
              })
            }
            else {
              taskResponse = await getOpenProject_ProjectTasks({
                ldapUserId, workPackagesLink, filterIssueCompleteShow, searchIssueText, reporter: (showCompleted || showPending) ? userDetails?.openProjectDetails?.id : '',
                showCompleted,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
                showPending, startDate, endDate, signal: abortController?.signal
              });
              handleJiraAssigneeError(taskResponse)
            }
            if (taskResponse?.message === "canceled") {
              isCanceled = true;
            }
            taskData = taskResponse?.data?._embedded?.elements || []
          } else {
            if (createdBy || allOpen || recentlyCreated || overDue) {
              taskResponse = await getOpenProjectFilteredTasks({
                id: userDetails?.openProjectDetails?.id,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
              });
            } else {
              taskResponse = await getOpenProject_ProjectTasks({
                ldapUserId, workPackagesLink, filterIssueTypeId, filterIssueCompleteShow, searchIssueText, reporter: (showCompleted || showPending) ? userDetails?.openProjectDetails?.id : '',
                showCompleted,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
                showPending, startDate, endDate, signal: abortController?.signal
              });
              handleJiraAssigneeError(taskResponse)
            }
            if (taskResponse?.message === "canceled") {
              isCanceled = true;
            }
            taskData = taskResponse?.data?._embedded?.elements || []
          }
        } else {
          if (isIssueTypeAll || filterIssueTypeId == 'ALL') {
            if (createdBy || allOpen || recentlyCreated || overDue) {
              taskResponse = await getOpenProjectFilteredTasks({
                id: userDetails?.openProjectDetails?.id,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
              })
            }
            else {
              taskResponse = await getOpenProjectAllUserTask({
                ldapUserId, reporter: (showCompleted || showPending) ? userDetails?.openProjectDetails?.id : '',
                showCompleted,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
                showPending, startDate, endDate, filterIssueCompleteShow, searchIssueText, signal: abortController?.signal
              });
            }
            if (taskResponse?.message === "canceled") {
              isCanceled = true;
            }
            taskData = taskResponse?.data?._embedded?.elements || []
          } else {
            if (createdBy || allOpen || recentlyCreated || overDue) {
              taskResponse = await getOpenProjectFilteredTasks({
                id: userDetails?.openProjectDetails?.id,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
              })
            }
            else {
              taskResponse = await getOpenProjectAllUserTask({
                ldapUserId, reporter: (showCompleted || showPending) ? userDetails?.openProjectDetails?.id : '',
                showCompleted,
                createdBy,
                allOpen,
                recentlyCreated,
                overDue,
                showPending, startDate, endDate, filterIssueTypeId, filterIssueCompleteShow, searchIssueText, signal: abortController?.signal
              });
            }
            if (taskResponse?.message === "canceled") {
              isCanceled = true;
            }
            taskData = taskResponse?.data?._embedded?.elements || []
          }
        }
      }

    } catch (error) {
      if (!refreshingInSetTimeOut) {
        taskData = [];
      }
    } finally {
      if (isCanceled) return;
      setLoading(false);
      setIsRefresh(false);
    }
    // Function to assign indentation levels for tree structure
    const setIndentLevel = (task: any, level: any) => {
      task.indentLevel = level;
      task.children.forEach((child: any) => setIndentLevel(child, level + 1));
    };

    if (Array.isArray(taskData) && taskData.length) {
      let taskMap = new Map();
      let tasks = [...taskData]
      let rootTasks:any = [];

      // First, map each task by ID and prepare empty relationships
      tasks.forEach((e, index) => {
        taskMap.set(e.id, {
          id: e.id,
          issue: e?.subject,
          key: e?.id,
          dueDate: e?.dueDate,
          createdAt: moment(e?.createdAt).format("YYYY-MM-DD HH:mm"),
          updatedAt: moment(e?.updatedAt).format("YYYY-MM-DD HH:mm"),
          priority: e?._links?.priority?.title ?? '',
          descriptionHTML: e?.description?.html,
          description: e?.description?.raw,
          projectLink: e?._links?.project?.href,
          workPackagesLink: e?._links?.self.href,
          taskTitle: e?.subject,
          projectTitle: e?._links?.project?.title,
          color: getColorBasedOnDueDate(e?.dueDate),
          task_project_commonFieldForSpentTime: e?._links?.project?.href, // project link is common field in open project task and project
          taskWebUrl: generateOpenProjectTaskUrl(e.id),
          issueData: e,
          dataGridId: index,
          issueType: e?._links?.type?.title,
          parentId: null,
          indentLevel: 0,
          children: [],
          createdBy: e?._links?.author?.title || ""
        });
      });

      // Now, establish parent-child relationships
      tasks.forEach((task) => {
        if (task._links.parent) {
          const parentId = parseInt(task._links.parent?.href?.split("/")?.pop());
          if (taskMap.has(parentId)) {
            taskMap.get(task.id).parentId = parentId;
            taskMap.get(parentId).children.push(taskMap.get(task.id));
          }
        }
      });

      // Determine root-level tasks (those without a parent)
      taskMap.forEach((task) => {
        if (!task.parentId) {
          setIndentLevel(task, 0);
          rootTasks.push(task);
        }
      });
      setTableRows(cloneDeep(rootTasks));
      setCloneTableData(cloneDeep(rootTasks));
    } else {
      if (!refreshingInSetTimeOut) {
        setTableRows([]);
      }
    }
  };

  const getJiraTaskList = async (isIssueTypeAll: boolean = false, abortController?: AbortController, loader: boolean = true, refreshingInSetTimeOut: boolean = false) => {
    let taskData
    let isCanceled: boolean = false;
    try {
      if (userDetails?.jiraDetails?.accountId) {
        const issueTypeId = isIssueTypeAll ? '' : filterIssueTypeId === 'ALL' ? '' : filterIssueTypeId
        // if(loader)setLoading(true);
        setIsRefresh(true);
        const response = await getJiraProjectTask({
          accountId: userDetails?.jiraDetails?.accountId,
          projectKey: projectSelectedProject?.projectKey || '',
          isListCompleted: filterIssueCompleteShow,
          filterIssueTypeId: issueTypeId,
          searchIssueText,
          reporter: (showCompleted || showPending) ? userDetails?.jiraDetails?.accountId : '',
          showCompleted,
          showPending,
          startDate,
          endDate,
          signal: abortController?.signal
        });
        if (response?.message === "canceled") {
          isCanceled = true;
        }
        if (!refreshingInSetTimeOut) {
          taskData = response?.data?.issues || [];
        } else {
          if (response.data && response.data.issues) {
            taskData = response.data.issues;
          }
        }
      }
    } catch (error) {
      if (!refreshingInSetTimeOut) {
        taskData = [];
      }
    } finally {
      if (isCanceled) return;
      setLoading(false);
      setIsRefresh(false);
    }

    if (Array.isArray(taskData) && taskData.length) {
      let taskList = taskData.map((e: any, index) => {
        if (e.id === issueState.id) {
          let obj: issueState = {
            id: e.id,
            issue: e?.fields.summary,
            key: e?.key,
            dueDate: e?.fields?.duedate,
            priority: e?.fields?.priority?.name,
            prioritisationPoints: e?.fields?.customfield_11188 ?? '',
            createdAt: moment(e?.fields?.created).format("YYYY-MM-DD HH:mm"),
            updatedAt: moment(e?.fields?.updated).format("YYYY-MM-DD HH:mm"),
            description: e?.fields?.description,
            projectLink: e?.fields?.project?.self,
            workPackagesLink: '',
            taskTitle: '',
            projectTitle: '',
            task_project_commonFieldForSpentTime: e?.fields?.project?.key,
            issueData: e,
            issueType: e?.fields?.issuetype?.name,
            createdBy: e?.fields?.creator?.displayName
          }
          dispatch(setStateIssue({ ...obj }));
        }
        return {
          id: e.id,
          issue: e?.fields.summary,
          key: e?.key,
          dueDate: e?.fields?.duedate,
          status: e?.fields?.status?.name,
          createdAt: moment(e?.fields?.created).format("YYYY-MM-DD HH:mm"),
          updatedAt: moment(e?.fields?.updated).format("YYYY-MM-DD HH:mm"),
          description: e?.fields?.description,
          projectLink: e?.fields?.project?.self,
          priority: e?.fields?.priority?.name,
          prioritisationPoints: e?.fields?.customfield_11188 ?? '',
          taskWebUrl: AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE + e?.key,
          color: getColorBasedOnDueDate(e?.fields?.duedate),
          task_project_commonFieldForSpentTime: e?.fields?.project?.key, // project key is common field in open project task and project
          issueData: e,
          dataGridId: index,
          issueType: e?.fields?.issuetype?.name,
          createdBy: e?.fields?.creator?.displayName
        };
      });
      setTableRows([...taskList]);
    } else {
      if (!refreshingInSetTimeOut) {
        setTableRows([]);
      }
    }

  };

  const getWaymoreTaskList = async (isIssueTypeAll: boolean, abortController?: AbortController, loader: boolean = true) => {
    let taskData = [];
    let isCanceled: boolean = false;
    try {
      if (userDetails?.userDetails) {
        if (loader) setLoading(true);
        setIsRefresh(true);
        const response = await getWaymoreCrmTasks(abortController?.signal);
        if (response?.message === "canceled") {
          isCanceled = true;
        }
        if (response && response.data && response.data.length) {
          const userTask = response?.data.filter((task: any) => task.email_address === userDetails?.userDetails?.email_address);
          taskData = userTask;
        }
      }
    } catch (error) {
      taskData = [];
    } finally {
      if (isCanceled) return;
      setLoading(false);
      setIsRefresh(false);
    }

    if (Array.isArray(taskData) && taskData.length) {
      let taskList = taskData.map((e: any, index) => {
        return {
          id: e.task_id,
          issue: e?.task_name,
          status: e?.status,
          startDate: e?.date_start,
          email: e?.email_address,
          issueData: e,
          accountName: e?.account_name,
          dataGridId: index,
          color: getColorBasedOnDueDate(e?.date_start)
        }
      });
      sortByStartDate(taskList);
      setTableRows([...taskList]);
    } else {
      setTableRows([]);
    }
  }


  const getIssueList = async (isIssueTypeAll: boolean = false, loader: boolean = true, refreshingInSetTimeOut: boolean = false) => {
    if (!isIssueTracking && !refreshingInSetTimeOut) {
      dispatch(clearStateIssue());
    }
    if (issueListAbortController) {
      issueListAbortController.abort();
    }
    const newIssueListAbortController = new AbortController();
    setIssueListAbortController(newIssueListAbortController);
    const jiraAccessToken = localStorage.getItem('jiraAccessToken');
    const openProjectAccessToken = localStorage.getItem('openProjectAccessToken');
    if (projectType === projectTypeMenu[0] && openProjectAccessToken) {
      await getOpenProjectTaskList(isIssueTypeAll, newIssueListAbortController, loader, refreshingInSetTimeOut);
    } else if (projectType === projectTypeMenu[2] && jiraAccessToken) {
      await getJiraTaskList(isIssueTypeAll, newIssueListAbortController, loader, refreshingInSetTimeOut);
    } else if (projectType === projectTypeMenu[1]) {
      await getWaymoreTaskList(isIssueTypeAll, newIssueListAbortController, loader);
    } else {
      setTableRows([]);
    }
  }



  const handleRowEnter = (params: any) => {
    setHoverId(params?.row?.id);
  };

  const handleRowLeave = () => {
    setHoverId(null);
  };

  const handelSelect = (rowData: any) => {
    dispatch(setStateIssue(rowData));
  };

  // State to track the last manually selected page
  const [lastPage, setLastPage] = useState<number>(0);

  const onTableResize = () => {
    if (issueState?.id) {
      const { pageSize, page } =
        apiRef.current?.state?.pagination?.paginationModel ?? {};

      const taskList =
        projectType === projectTypeMenu[0]
          ? getVisibleRows(tableRows)
          : tableRows ?? [];

      // Assuming the selected row's ID is in `issueState`
      const selectedRowIndex = taskList.findIndex(
        (row: any) => row.id === issueState.id
      );

      if (selectedRowIndex >= 0) {
        // Calculate the page index for the selected row
        const selectedPage = Math.floor(selectedRowIndex / pageSize);

        // If the selected row is not on the current page, adjust the page
        if (selectedPage !== page && selectedPage !== lastPage) {
          apiRef.current.setPage(selectedPage);
          setLastPage(selectedPage); // Update the last known page
        }
      }
    } else {
      // If no issueState, reset to the first page
      apiRef.current?.setPage?.(0);
      setLastPage(0); // Reset the last known page to the first page
    }
  };

  const getRowClassName = (param: GridRowParams): string => {
    const color = param.row?.color
    return `custom-row-class-${color}`
  }

  const getDataGridId = (row: any) => row?.dataGridId;

  const startCreatedMeeting = () => {
    dispatch(setTrackingState(true));
    dispatch(setIsMeetingTaskRunning(true));
  };

  const fetchAndRunCreatedMeeting = async (createdTaskId: string) => {
    await getIssueList(true);
    setTimeout(() => {
      let createTaskRowData;
      setTableRows((rows: any) => {
        createTaskRowData = rows?.find((row: any) => row?.id == createdTaskId)
        return rows;
      })
      handelSelect(createTaskRowData);
      startCreatedMeeting();
    }, 100)
  };

  const CustomColumnMenuWrapper: React.FC<GridColumnMenuProps> = (
    props: any
  ) => {
    return props.colDef?.field === "dueDate" ? (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuUserItem: CustomColumnMenu,
        }}
        slotProps={{
          columnMenuUserItem: {
            displayOrder: 100,
            CustomDateFormat: alternativeFormat,
            CustomDateHandler: () => {
              setDateFormat(alternativeFormat);
              props.hideMenu?.();
            },
          },
        }}
      />
    ) : (
      <GridColumnMenu {...props} />
    );
  };
  const handleRefresh = () => {
    getIssueList(undefined, false, true); /* Third params is to indicate that the API call is performed by setTimeOut */
  };

  const slotComponents: any = {
    footer: CustomFooter,
    columnMenu: CustomColumnMenuWrapper,
  };

  const slotProps: any = {
    footer: { onRefresh: handleRefresh, isRefresh },
  };

  // interval for get list api call
  useEffect(() => {
    let timer = setInterval(() => {
      setIntervalCall({ refetch: true })
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(timer);
      setIntervalCall({})
    }
  }, [isRefresh]);

  // api call interval change
  useEffect(() => {
    if (intervalCall?.refetch) {
      handleRefresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalCall])

  useEffect(() => {
    apiRef.current.subscribeEvent('rowMouseLeave', handleRowLeave);
    apiRef.current.subscribeEvent('rowMouseEnter', handleRowEnter);
    return;
  }, [apiRef]);


  useEffect(() => {
    if (issueRefetch) {
      getIssueList(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueRefetch])

  useEffect(() => {
    if (createdMeetingId) {
      fetchAndRunCreatedMeeting(createdMeetingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdMeetingId]);

  useEffect(() => {
    if (userDetails && (filterIssueTypeId || showCompleted || showPending || createdBy || overDue || allOpen || recentlyCreated)) {
      getIssueList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIssueTypeId, showCompleted, showPending, createdBy, overDue, allOpen, recentlyCreated])


  useEffect(() => {
    if (userDetails) {
      getIssueList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSelectedProject, filterIssueCompleteShow, startDate, endDate])

  useEffect(() => {
    if (userDetails && projectType) {
      if (filterIssueTypeId === "ALL") {
        getIssueList(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, projectType])

  const debouncedSearch = useCallback(
    _.debounce(async () => {
      getIssueList();
    }, 2000),
    [getIssueList]
  );

  useEffect(() => {
    if (userDetails && (projectType === projectTypeMenu[2] || projectType === projectTypeMenu[0])) {
      setLoading(true);
      debouncedSearch();
    }
    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchIssueText]);

  const groupAndCountStatus = (taskList: any) => {
    const statusCount = taskList.reduce((acc: any, task: any) => {
      acc[task.status] = (acc[task.status] || 0) + 1;
      return acc;
    }, {});

    setTaskSummary(statusCount);
  };

  // useEffect(() => {
  //   groupAndCountStatus(tableRows);
  // }, [tableRows])

  const handleFetchTree = async () => {
    const selectedTask = await handleTreeToggleOpen(tableRows, selectedNotification);
    if(selectedTask?.data) {
      setExpandedRows({...selectedTask?.ids});
      handelSelect(selectedTask?.data);
    }
  }

  useEffect(() => {
    if (selectedNotification && tableRows.length > 0) {
      handleFetchTree();
    }
  }, [selectedNotification, tableRows]);

  return (
    <div className="issue-table-container" style={{ height: issueState?.issue ? "47%" : "100%" }}>
      {loading && (
        <div className="circular-progress-loader-backdrop">
          <div className="circular-progress-loader" >
            <CircularProgress />
          </div>
        </div>
      )}
      <DataGrid
        className="issue-table"
        apiRef={apiRef}
        rows={projectType === projectTypeMenu[0] ? getVisibleRows(tableRows): tableRows}
        columns={getDataGridColumns()}
        autoPageSize
        getRowId={getDataGridId}
        onRowClick={(data) => {
          handelSelect(data?.row);
          setSelectedNotification(null);
        }}
        onResize={onTableResize}
        slots={slotComponents}
        slotProps={slotProps}
        sx={{
          color: 'black',
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#cde2f7',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          }
        }}
        getRowClassName={getRowClassName}
        {...(projectType === projectTypeMenu[0] ?
          {
            sortingMode: "server",
            sortModel: sortModel,
            onSortModelChange: handleSortModelChange,
          } : {})
        }
      />
      <TimeTrackingDialog 
        isSubTask={isSubTask}
        open={isDialogOpen}
        onClose={handleDialogClose}
        onCreateTask={handleTaskCreation}
        assigneeName={issueState?.issueData?.fields?.assignee?.displayName}
      />
      <CreateJiraTaskDialog
        isSubTask={isSubTask}
        isOpen={isJiraTaskDialogOpen}
        onClose={(...args) => handleCreateTaskDialogClose(...args)}
      />
      <OpenProjectCreateTaskDialog
        isSubTask={isSubTask}
        isOpen={isOPTaskDialogOpen}
        onClose={handleCreateTaskDialogClose}
      />
      <TalkToAiDialog isOpen={openTalkToAI} onClose={handleTalkToAiClose} selectedIssue={selectedIssue} />
    </div>
  )
}

export default IssueTablePage